import { useState } from 'react';
import { saveCategory, deleteCategory } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

type Category = { id: string; name: string; description: string; displayOrder: number; requiredForAllStaff: boolean };

interface Props {
  categories: Category[];
  onRefresh: () => void;
}

export default function CategoryManager({ categories, onRefresh }: Props) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ categoryName: '', description: '', displayOrder: 0, requiredForAllStaff: false });
  const [saving, setSaving] = useState(false);

  const openNew = () => {
    setEditId(null);
    setForm({ categoryName: '', description: '', displayOrder: categories.length + 1, requiredForAllStaff: false });
    setAdding(true);
  };

  const openEdit = (c: Category) => {
    setEditId(c.id);
    setForm({ categoryName: c.name, description: c.description, displayOrder: c.displayOrder, requiredForAllStaff: c.requiredForAllStaff });
    setAdding(true);
  };

  const save = async () => {
    if (!form.categoryName.trim()) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      await saveCategory({ id: editId || undefined, ...form });
      toast.success(editId ? 'Category updated' : 'Category created');
      setAdding(false);
      setEditId(null);
      onRefresh();
    } catch { toast.error('Failed to save category'); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await deleteCategory({ id });
      toast.success('Category deleted');
      onRefresh();
    } catch { toast.error('Failed to delete — may have linked modules'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{categories.length} categories</p>
        <Button onClick={openNew} size="sm" className="bg-primary text-primary-foreground">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Category
        </Button>
      </div>

      {adding && (
        <div className="border border-primary/30 bg-primary/5 p-4 mb-4 space-y-3">
          <h4 className="text-sm font-semibold text-foreground">{editId ? 'Edit Category' : 'New Category'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
              <Input value={form.categoryName} onChange={e => setForm(f => ({ ...f, categoryName: e.target.value }))} placeholder="e.g. Service & Floor" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Display Order</label>
              <Input type="number" value={form.displayOrder} onChange={e => setForm(f => ({ ...f, displayOrder: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="min-h-16" placeholder="What does this category cover?" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="req" checked={form.requiredForAllStaff} onChange={e => setForm(f => ({ ...f, requiredForAllStaff: e.target.checked }))} className="accent-primary" />
            <label htmlFor="req" className="text-sm text-foreground">Required for all staff</label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground">
              <Check className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setEditId(null); }}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Category</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Description</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-muted-foreground">Order</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-muted-foreground hidden sm:table-cell">All Staff</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {categories.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">No categories yet. Add one above.</td></tr>
            )}
            {categories.map(c => (
              <tr key={c.id}>
                <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell truncate max-w-xs">{c.description || '—'}</td>
                <td className="px-4 py-3 text-xs text-center text-muted-foreground">{c.displayOrder}</td>
                <td className="px-4 py-3 text-center hidden sm:table-cell">
                  {c.requiredForAllStaff ? <span className="text-xs text-primary">✓</span> : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)}><Pencil className="w-3 h-3" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(c.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
